import { api } from './api'
import { Consultation, ConsultationFormData, ConsultationFilters, ConsultationArtefact, ConsultationCode } from '../types/consultation'

export const consultationService = {
  /**
   * Get all consultations with optional filters
   */
  getAll: async (filters?: ConsultationFilters): Promise<Consultation[]> => {
    const response = await api.get<Consultation[]>('/consultations', {
      params: filters,
    })
    return response.data
  },

  /**
   * Get consultation by ID
   */
  getById: async (id: string): Promise<Consultation> => {
    const response = await api.get<Consultation>(`/consultations/${id}`)
    return response.data
  },

  /**
   * Get consultations for a specific patient
   */
  getByPatient: async (patientId: string): Promise<Consultation[]> => {
    const response = await api.get<Consultation[]>(`/consultations`, {
      params: { patientId },
    })
    return response.data
  },

  /**
   * Get consultations for a specific staff member
   */
  getByStaff: async (staffId: string): Promise<Consultation[]> => {
    const response = await api.get<Consultation[]>(`/consultations`, {
      params: { staffId },
    })
    return response.data
  },

  /**
   * Create a new consultation
   */
  create: async (data: ConsultationFormData): Promise<Consultation> => {
    const response = await api.post<Consultation>('/consultations', data)
    return response.data
  },

  /**
   * Update a consultation
   */
  update: async (id: string, data: Partial<ConsultationFormData>): Promise<Consultation> => {
    const response = await api.put<Consultation>(`/consultations/${id}`, data)
    return response.data
  },

  /**
   * Complete a consultation (change status to completed)
   */
  complete: async (
    id: string,
    data?: { 
      reason?: string
      notes?: string
      diagnosis?: string[]
      plan?: string
      codes?: ConsultationCode[]
      patientInstructions?: string
      instructionsGiven?: boolean
      instructionsGivenAt?: string
      instructionsGivenBy?: string
    },
  ): Promise<Consultation> => {
    const response = await api.put<Consultation>(`/consultations/${id}/complete`, data)
    return response.data
  },

  /**
   * Cancel a consultation
   */
  cancel: async (id: string, reason?: string): Promise<Consultation> => {
    const response = await api.put<Consultation>(`/consultations/${id}/cancel`, { reason })
    return response.data
  },

  /**
   * Delete a consultation
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/consultations/${id}`)
  },

  /**
   * Add an artefact to a consultation
   */
  addArtefact: async (consultationId: string, artefact: Omit<ConsultationArtefact, 'id' | 'consultationId' | 'createdAt' | 'updatedAt'>): Promise<ConsultationArtefact> => {
    const response = await api.post<ConsultationArtefact>(`/consultations/${consultationId}/artefacts`, artefact)
    return response.data
  },

  /**
   * Update an artefact
   */
  updateArtefact: async (consultationId: string, artefactId: string, artefact: Partial<ConsultationArtefact>): Promise<ConsultationArtefact> => {
    const response = await api.put<ConsultationArtefact>(`/consultations/${consultationId}/artefacts/${artefactId}`, artefact)
    return response.data
  },

  /**
   * Delete an artefact
   */
  deleteArtefact: async (consultationId: string, artefactId: string): Promise<void> => {
    await api.delete(`/consultations/${consultationId}/artefacts/${artefactId}`)
  },
}

















